import { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { debounce } from 'lodash';

const useScrollPercentTracking = (trigger?: (scrollPercent: number) => void) => {
  const scrollPercent = useRef(0);

  const [pageHeight, setPageHeight] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);

  const thresholds = [25, 50, 75, 100];

  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const footer = document.querySelector('#footer-wrapper')?.clientHeight || 0;
    const totalScrollable = pageHeight - viewportHeight - footer;
    if (totalScrollable <= 0) return;

    const currentPercent = Math.round((scrollTop / totalScrollable) * 100);

    thresholds.some((threshold) => {
      if (currentPercent >= threshold && scrollPercent.current < threshold) {
        scrollPercent.current = threshold;
        return true;
      }
      return false;
    });
  }, [pageHeight, viewportHeight]);

  const debouncedHandleScroll = useMemo(() => debounce(handleScroll, 20), [handleScroll]);

  useEffect(() => {
    const updatePageHeight = () => {
      const height = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.offsetHeight,
        document.body.clientHeight,
        document.documentElement.clientHeight,
      );
      setPageHeight(height);
      setViewportHeight(window.innerHeight);
    };
    updatePageHeight();

    window.addEventListener('resize', updatePageHeight);
    window.addEventListener('scroll', debouncedHandleScroll);

    return () => {
      window.removeEventListener('resize', updatePageHeight);
      window.removeEventListener('scroll', debouncedHandleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    return () => {
      if (trigger && scrollPercent.current) {
        trigger(scrollPercent.current);
      }
    };
  }, []);

  return scrollPercent.current;
};

export default useScrollPercentTracking;
